<template>
  <div
    class="modal modal-large show"
    :class="{ 'expand-modal': !lodash.isEmpty(company) }"
  >
    <!-- Header -->
    <ModalHeader :title="title" @closeModal="closeModal"></ModalHeader>

    <!-- Loader -->
    <div
      v-if="lodash.isEmpty(company) || showOptions.pageLoader"
      class="modal-body"
    >
      <PageLoader :showLoader="true"></PageLoader>
    </div>

    <div v-else class="modal-body">
      <!--START: Left Menu-->
      <div class="side-menu">
        <div class="page-menu">
          <div class="page-menu-category">
            <label>Settings</label>
            <div
              v-for="item in menuItems"
              :key="item.key"
              class="page-menu-item"
              :class="{ 'router-link-exact-active': activeTab === item.key }"
              @click="activeTab = item.key"
            >
              <unicon :name="item.icon"></unicon>
              <span>{{ item.name }}</span>
            </div>
          </div>
        </div>
      </div>
      <!--END: Left Menu-->

      <!--START: Content-->
      <div class="settings-content">
        <!--START: User Access-->
        <div v-if="activeTab === 'user-access'" class="users-main">
          <PageHeader
            :content="headerContent"
            :showButton="false"
            :showHeaderShadow="showOptions.headerShadow"
            buttonTitle="Add Admin"
            @buttonClicked="showAddUserModal()"
          ></PageHeader>

          <!-- Users Access -->
          <UserAccessTable v-if="users.length" :users="users"></UserAccessTable>

          <!-- Zero State -->
          <ZeroState
            v-else
            class="zero-state"
            :imageURL="zeroStateData.imageURL"
            :title="zeroStateData.title"
            :message="zeroStateData.message"
            :buttonTitle="zeroStateData.buttonTitle"
            @eventHandler="showAddUserModal"
          ></ZeroState>

          <!-- Add User-->
          <AddUserModal
            :show="showOptions.addUserModal"
            @addUser="addUser"
            @closeModal="closeUserModal"
          ></AddUserModal>
          <div
            class="bg-overlay light"
            :class="{ show: showOptions.addUserModal }"
            @click="closeModal"
          ></div>
        </div>
        <!--END: User Access-->

        <!--START: Data Security-->
        <div v-if="activeTab === 'data-security'" class="security-main">
          <DataSecurity @sendAccountRequest="sendAccountRequest"></DataSecurity>
        </div>
        <!--END: Data Security-->

        <!--START: Integrations-->
        <div v-if="activeTab === 'integrations'" class="integrations-main">
          <IntegrationsPanel />
        </div>
        <!--END: Integrations-->
      </div>
      <!--END: Content-->

      <!-- Notification Message-->
      <NotificationMessage
        :show="status.show"
        :status="status.status"
        :title="status.title"
      ></NotificationMessage>

      <!-- Loader-->
      <LineLoader :show="showOptions.lineLoader"></LineLoader>
    </div>
  </div>
</template>

<script>
// Import libraries
import _ from "lodash";

// Importing components
import ModalHeader from "@/components/modals/ModalHeader";
import PageHeader from "@/components/dashboard/PageHeader";
import NotificationMessage from "@/components/modals/NotificationMessage";
import LineLoader from "@/components/loaders/LineLoader";
import PageLoader from "@/components/loaders/PageLoader";
import ZeroState from "@/components/misc/ZeroState";

import UserAccessTable from "@/components/dashboard/tables/UserAccessTable";
import AddUserModal from "@/components/dashboard/userAccess/AddUserModal";

import DataSecurity from "@/components/dashboard/DataSecurity";
import IntegrationsPanel from "@/components/dashboard/integrations/IntegrationsPanel";

// Importing Services
import "external-svg-loader";
import { UserService, CompanyService } from "@/services";

export default {
  name: "AdminSettings",
  data() {
    return {
      lodash: _,
      title: "Settings",
      activeTab: "user-access",
      menuItems: [
        { key: "user-access", name: "User Access", icon: "users-alt" },
        { key: "data-security", name: "Data Security", icon: "lock" },
        { key: "integrations", name: "Integrations", icon: "plug" },
      ],
      showOptions: {
        headerShadow: false,
        lineLoader: false,
        pageLoader: true,
        addUserModal: false,
      },
      headerContent: {
        title: "User Access",
        description: "Manage user access to the Macha dashboard",
      },
      users: [],
      status: {
        show: false,
        status: "success",
        title: "",
      },
      zeroStateData: {
        imageURL: "services.jpg",
        title: "No users in your team",
        message: "Add users to your team to give them dashboard access",
        buttonTitle: "Add User",
      },
    };
  },
  props: {
    initialTab: {
      type: String,
      default: "user-access",
    },
  },
  watch: {},
  components: {
    ModalHeader,
    PageHeader,
    NotificationMessage,
    LineLoader,
    PageLoader,
    ZeroState,
    UserAccessTable,
    AddUserModal,
    DataSecurity,
    IntegrationsPanel,
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
  },
  async created() {
    this.getUsers();
  },
  methods: {
    // Get users
    async getUsers() {
      const response = await UserService.GetUsers();
      if (!response.hasError) {
        this.users = response.data;
        this.users = _.orderBy(this.users, ["name"], ["desc"]);
      }

      this.showOptions.pageLoader = false;
    },

    closeUserModal() {
      this.showOptions.addUserModal = false;
    },

    // Add a new user
    async addUser(user) {
      this.closeUserModal();

      this.showOptions.pageLoader = true;
      this.users.splice(0);

      const data = { users: [user] };
      await UserService.AddUsers(data);
      await this.getUsers();

      this.showOptions.pageLoader = false;
    },

    // Add user
    showAddUserModal() {
      this.showOptions.addUserModal = true;
    },

    // Send a notification to team for account actions
    async sendAccountRequest(actionType) {
      this.showOptions.lineLoader = true;

      const data = { actionType };
      await CompanyService.SendAccountRequest(data);

      this.showOptions.lineLoader = false;
      this.status.title = "We've received your request and will get back";
      this.showStatusMessage(this.status, 2500);
    },

    // Close the add modal
    closeModal() {
      this.$emit("closeModal");
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";

.header-wrapper {
  border-bottom: none;
}

.zero-state {
  margin-top: 2.5rem;
}

.page-loader-wrapper {
  margin: auto;
}

.expand-modal.modal {
  width: 80% !important;
  height: 90% !important;
  overflow: hidden;
}

.modal-body {
  display: flex;
  flex-direction: row;
  align-items: stretch;
  overflow: hidden !important;
  background: $whiteColor;
  gap: 0;
}

.side-menu {
  flex: 0 0 13rem;
  padding: 1rem 0.75rem;
  padding-left: 0;
  border-right: 1px solid $greyBorderColor;
  overflow-y: auto;

  .page-menu {
    flex: 1;
  }

  .page-menu-category {
    margin-bottom: 2.5rem;

    label {
      display: block;
      color: lighten($paragraphColor, 15%);
      font-weight: $mediumFontWeight;
      font-size: $smallestFontSize;
      margin-bottom: 1rem;
    }
  }

  .page-menu-item {
    display: flex;
    flex-direction: row;
    align-items: center;
    padding: 0.45rem 0.85rem;
    margin-bottom: 0.15rem;
    border-radius: 0.35rem;
    cursor: pointer;
    @include transition;

    span {
      font-size: $smallFontSize;
      color: $greyColor;
    }

    .unicon ::v-deep svg {
      fill: lighten($greyColor, 40%);
      height: auto;
      width: 1rem;
      margin-right: 0.75rem;
    }

    &:hover {
      background-color: lighten($lightGreyColor, 30%);
    }

    &.router-link-exact-active {
      background-color: lighten($lightGreyColor, 30%);

      span {
        font-weight: $mediumFontWeight;
      }
    }
  }
}

.settings-content {
  flex: 1;
  overflow-y: auto;
  padding: 1rem 1.5rem;
  min-width: 0;
}

.users-main {
  display: flex;
  flex-direction: column;
  overflow: hidden;
  max-height: 70vh;
}

.security-main {
  max-height: 70vh;
  overflow-y: auto;
}

.integrations-main {
  max-height: 70vh;
  overflow-y: auto;
}

::v-deep .line-loader {
  position: fixed;
  z-index: 101;
  bottom: 0;
  left: 0;
}

.show-shadow {
  box-shadow: 0 0.5rem 0.7rem -0.5rem $boxShadowColor;
}

.settings-form.card {
  overflow-y: scroll;
}
</style>
